/**
 * Auto-Learn / Native-Reflection controller.
 *
 * Extracted verbatim from interactive-mode.ts (god-file decomposition). Owns the disk-backed
 * Auto Learn run-state machine (state.json + lockfile), the background-learner spawn/prune
 * lifecycle, and the interactive task-contract nudge. It takes narrow deps (a live session
 * accessor, a self-modification-source resolver that stays in the host, and a small UI callback
 * surface) rather than the whole InteractiveMode instance.
 */

import * as crypto from "node:crypto";
import * as fs from "node:fs";
import * as path from "node:path";
import type { AgentMessage } from "@caupulican/pi-agent-core";
import { isAutoLearnSessionId } from "@caupulican/pi-agent-core/session";
import type { Model } from "@caupulican/pi-ai";
import type { SelectItem } from "@caupulican/pi-tui";
import { spawn } from "child_process";
import lockfile from "proper-lockfile";
import { getAgentDir } from "../../config.ts";
import type { AgentSession } from "../../core/agent-session.ts";
import { readAutoLearnSessionIdFromFile, reportCompletedAutoLearnUsageHelper } from "../../core/cost/session-usage.ts";
import {
	getAutoLearnPreset,
	isCurrentSessionReflectionEnabled,
	resolveAutoLearnSettings,
} from "../../core/learning/auto-learn-settings.ts";
import { isProcessAlive } from "../../core/process-liveness.ts";
import {
	describeInFlightWorkUnit,
	getInFlightWorkUnits,
	getPendingReloadBlockers,
} from "../../core/reload-blockers.ts";
import { PI_SESSION_ROLE_ENV } from "../../core/session-role.ts";
import type { AutoLearnSettings, AutonomyMode } from "../../core/settings-manager.ts";
import {
	checkTaskStepsContract,
	INITIAL_TASK_CONTRACT_STREAK,
	type TaskContractStreak,
} from "../../core/tasks/task-contract-monitor.ts";
import { getProcessWorkRun } from "../../utils/work-directory.ts";
import { theme } from "./theme/theme.ts";

export const AUTONOMY_MODES: AutonomyMode[] = ["off", "safe", "balanced", "full"];
const AUTO_LEARN_RESERVATION_MS = 2 * 60 * 1000;
export const AUTO_LEARN_HISTORY_RETENTION_MS = 7 * 24 * 60 * 60 * 1000;

export interface AutoLearnHistoryPruneResult {
	promptFiles: number;
	logFiles: number;
	sessionFiles: number;
	errors: number;
}

export interface AutoLearnHistoryPruneOptions {
	dataDir: string;
	now?: number;
	retentionMs?: number;
	activeRunIds?: Iterable<string | undefined>;
	activeSessionIds?: Iterable<string | undefined>;
}

type AutoLearnHistoryPruneCounter = Exclude<keyof AutoLearnHistoryPruneResult, "errors">;

function definedStringSet(values: Iterable<string | undefined> | undefined): Set<string> {
	const set = new Set<string>();
	if (!values) return set;
	for (const value of values) {
		if (typeof value === "string" && value.length > 0) set.add(value);
	}
	return set;
}

function sanitizeAutoLearnPathPart(input: string | undefined, fallback: string): string {
	const cleaned = (input || fallback)
		.replace(/[^a-zA-Z0-9._-]+/g, "-")
		.replace(/-+/g, "-")
		.replace(/^-+|-+$/g, "")
		.slice(0, 80);
	return cleaned || fallback;
}

function isOldAutoLearnArtifact(filePath: string, now: number, retentionMs: number): boolean {
	const stats = fs.lstatSync(filePath);
	return stats.isFile() && now - stats.mtimeMs > retentionMs;
}

function removeOldAutoLearnArtifact(
	filePath: string,
	result: AutoLearnHistoryPruneResult,
	counter: AutoLearnHistoryPruneCounter,
): void {
	try {
		fs.rmSync(filePath, { force: true });
		result[counter]++;
	} catch {
		result.errors++;
	}
}

function isPathInside(target: string, root: string): boolean {
	const resolvedTarget = path.resolve(target);
	const resolvedRoot = path.resolve(root);
	return resolvedTarget === resolvedRoot || resolvedTarget.startsWith(`${resolvedRoot}${path.sep}`);
}

function removeAutoLearnArtifactPath(filePath: string, root: string): boolean {
	if (!isPathInside(filePath, root)) return false;
	try {
		fs.rmSync(filePath, { recursive: true, force: true });
		return true;
	} catch {
		return false;
	}
}

function getAutoLearnSessionIdFromFileName(fileName: string): string | undefined {
	return fileName.match(/_(auto-learn-[A-Za-z0-9._-]+)\.jsonl$/)?.[1];
}

function readAutoLearnDirectoryEntries(dir: string): fs.Dirent[] {
	try {
		return fs.readdirSync(dir, { withFileTypes: true });
	} catch {
		return [];
	}
}

function visitAutoLearnFiles(root: string, visit: (filePath: string, fileName: string) => void): void {
	const pendingDirectories = [root];
	while (pendingDirectories.length > 0) {
		const dir = pendingDirectories.pop()!;
		for (const entry of readAutoLearnDirectoryEntries(dir)) {
			const filePath = path.join(dir, entry.name);
			if (entry.isDirectory()) {
				pendingDirectories.push(filePath);
			} else if (entry.isFile()) {
				visit(filePath, entry.name);
			}
		}
	}
}

function shouldPruneAutoLearnArtifact(
	filePath: string,
	now: number,
	retentionMs: number,
	result: AutoLearnHistoryPruneResult,
): boolean {
	try {
		return isOldAutoLearnArtifact(filePath, now, retentionMs);
	} catch {
		result.errors++;
		return false;
	}
}

function pruneAutoLearnSessionFiles(
	dir: string,
	activeSessionIds: ReadonlySet<string>,
	now: number,
	retentionMs: number,
	result: AutoLearnHistoryPruneResult,
): void {
	visitAutoLearnFiles(dir, (filePath, fileName) => {
		if (!fileName.endsWith(".jsonl") || !shouldPruneAutoLearnArtifact(filePath, now, retentionMs, result)) return;
		const sessionId = readAutoLearnSessionIdFromFile(filePath) ?? getAutoLearnSessionIdFromFileName(fileName);
		if (!sessionId || !isAutoLearnSessionId(sessionId) || activeSessionIds.has(sessionId)) return;
		removeOldAutoLearnArtifact(filePath, result, "sessionFiles");
	});
}

function pruneAutoLearnRunArtifacts(
	dir: string,
	activeRunIds: ReadonlySet<string>,
	now: number,
	retentionMs: number,
	result: AutoLearnHistoryPruneResult,
): void {
	visitAutoLearnFiles(dir, (filePath, fileName) => {
		const promptRunId = fileName.endsWith(".prompt.md") ? fileName.slice(0, -".prompt.md".length) : undefined;
		const logRunId = fileName.endsWith(".log") ? fileName.slice(0, -".log".length) : undefined;
		const runId = promptRunId ?? logRunId;
		if (!runId || activeRunIds.has(runId) || !shouldPruneAutoLearnArtifact(filePath, now, retentionMs, result))
			return;
		removeOldAutoLearnArtifact(filePath, result, promptRunId ? "promptFiles" : "logFiles");
	});
}

export function pruneAutoLearnConversationHistory(options: AutoLearnHistoryPruneOptions): AutoLearnHistoryPruneResult {
	const result: AutoLearnHistoryPruneResult = { promptFiles: 0, logFiles: 0, sessionFiles: 0, errors: 0 };
	const dataDir = path.resolve(options.dataDir);
	const now = options.now ?? Date.now();
	const retentionMs = options.retentionMs ?? AUTO_LEARN_HISTORY_RETENTION_MS;
	const activeRunIds = definedStringSet(options.activeRunIds);
	const activeSessionIds = definedStringSet(options.activeSessionIds);
	if (retentionMs <= 0 || !fs.existsSync(dataDir)) return result;

	pruneAutoLearnRunArtifacts(dataDir, activeRunIds, now, retentionMs, result);
	pruneAutoLearnSessionFiles(dataDir, activeSessionIds, now, retentionMs, result);
	return result;
}

export interface AutoLearnRunRecord {
	tenant: string;
	pid?: number;
	model: string;
	reason: string;
	startedAt: number;
	expiresAt: number;
	cwd: string;
	logPath: string;
	sessionDir?: string;
	sessionId?: string;
	promptPath?: string;
	kind?: "auto" | "reflection";
	autonomyMode?: AutonomyMode;
	authority?: string;
	status?: "reserved" | "running";
}

export interface AutoLearnState {
	lastLaunchByTenant?: Record<string, number>;
	lastReflectionByTenant?: Record<string, number>;
	runs?: Record<string, AutoLearnRunRecord>;
}

interface AutoLearnStateLockResult<T> {
	result: T;
	next?: AutoLearnState;
}

interface AutoLearnDecision {
	shouldRun: boolean;
	reason: string;
	messageCount: number;
	contextPercent: number | null;
	cooldownRemainingMs: number;
	runningCount: number;
	bypassCooldown?: boolean;
}

interface AutoLearnReservation {
	runId: string;
	startedAt: number;
}

type AutoLearnReservationResult = { ok: true; reservation: AutoLearnReservation } | { ok: false; reason: string };

export interface AutoLearnSpawnTarget {
	command: string;
	argsPrefix: string[];
}

export interface AutoLearnSpawnArgsOptions {
	name: string;
	modelPattern: string;
	thinkingLevel?: string;
	sessionDir: string;
	sessionId: string;
	promptPath: string;
}

export function buildAutoLearnSpawnArgs(
	spawnTarget: AutoLearnSpawnTarget,
	options: AutoLearnSpawnArgsOptions,
): string[] {
	return [
		...spawnTarget.argsPrefix,
		"--print",
		"--name",
		options.name,
		"--model",
		options.modelPattern,
		...(options.thinkingLevel ? ["--thinking", options.thinkingLevel] : []),
		"--session-dir",
		options.sessionDir,
		"--session-id",
		options.sessionId,
		`@${options.promptPath}`,
	];
}

export function findAutoLearnSpawnNullByteInput(command: string, args: readonly string[]): string | undefined {
	if (command.includes("\0")) return "command";
	const argIndex = args.findIndex((arg) => arg.includes("\0"));
	return argIndex === -1 ? undefined : `args[${argIndex}]`;
}

export interface AutoLearnControllerUi {
	showStatus(message: string): void;
	readonly footerDataProvider: { setExtensionStatus(key: string, text: string | undefined): void };
	invalidateFooter(): void;
	requestRender(): void;
}

export interface AutoLearnControllerDeps {
	getSession(): AgentSession;
	resolveSelfModificationSource(settings: { sourcePath?: string; sourcePaths?: string[] }): string | undefined;
	ui: AutoLearnControllerUi;
}

export class AutoLearnController {
	// Cheap turn-boundary task_steps contract nudge. Tracks the consecutive-violation streak
	// across turns so `checkTaskStepsContractNudge` (invoked from the existing per-turn `agent_end`
	// pass below) can fire exactly one advisory harness note per sustained-violation streak. It makes
	// no model call and remains independent of current-session reflection cue planning.
	private _taskContractStreak: TaskContractStreak = INITIAL_TASK_CONTRACT_STREAK;

	private readonly deps: AutoLearnControllerDeps;

	constructor(deps: AutoLearnControllerDeps) {
		this.deps = deps;
	}

	private get session(): AgentSession {
		return this.deps.getSession();
	}

	private getAutoLearnModelAuthPriority(model: Model<any>): number {
		if (this.session.model && model.provider === this.session.model.provider && model.id === this.session.model.id) {
			return 0;
		}

		const credential = this.session.modelRegistry.authStorage.get(model.provider);
		if (credential?.type === "oauth") return 1;
		if (credential?.type === "api_key") return 2;

		const authStatus = this.session.modelRegistry.getProviderAuthStatus(model.provider);
		switch (authStatus.source) {
			case "runtime":
				return 3;
			case "environment":
				return 4;
			case "models_json_key":
			case "models_json_command":
			case "fallback":
				return 5;
			default:
				return 6;
		}
	}

	private getAutoLearnModelAuthLabel(model: Model<any>): string {
		const credential = this.session.modelRegistry.authStorage.get(model.provider);
		if (credential?.type === "oauth") return "subscription";
		if (credential?.type === "api_key") return "API key";

		const authStatus = this.session.modelRegistry.getProviderAuthStatus(model.provider);
		switch (authStatus.source) {
			case "runtime":
				return authStatus.label ? `runtime ${authStatus.label}` : "runtime API key";
			case "environment":
				return authStatus.label ? `env ${authStatus.label}` : "environment API key";
			case "models_json_key":
				return "models.json API key";
			case "models_json_command":
				return "models.json command";
			case "fallback":
				return authStatus.label ?? "custom provider config";
			default:
				return "configured";
		}
	}

	getAutoLearnModelOptions(): SelectItem[] {
		this.session.modelRegistry.refresh();
		const availableModels = this.session.modelRegistry.getAvailable();
		const sortedModels = [...availableModels].sort((a, b) => {
			const priorityDelta = this.getAutoLearnModelAuthPriority(a) - this.getAutoLearnModelAuthPriority(b);
			if (priorityDelta !== 0) return priorityDelta;
			const providerDelta = this.session.modelRegistry
				.getProviderDisplayName(a.provider)
				.localeCompare(this.session.modelRegistry.getProviderDisplayName(b.provider));
			if (providerDelta !== 0) return providerDelta;
			return a.id.localeCompare(b.id);
		});

		return sortedModels.map((model) => {
			const providerName = this.session.modelRegistry.getProviderDisplayName(model.provider);
			const authLabel = this.getAutoLearnModelAuthLabel(model);
			const modelPattern = `${model.provider}/${model.id}`;
			const currentLabel =
				this.session.model && model.provider === this.session.model.provider && model.id === this.session.model.id
					? " · current"
					: "";
			const displayName = model.name && model.name !== model.id ? ` · ${model.name}` : "";
			return {
				value: modelPattern,
				label: modelPattern,
				description: `${providerName} · ${authLabel}${currentLabel}${displayName}`,
			};
		});
	}

	getAutoLearnDataDir(): string {
		return getProcessWorkRun(getAgentDir(), "learning", "auto-learn", "state").path;
	}

	private getAutoLearnStatePath(): string {
		return path.join(this.getAutoLearnDataDir(), "state.json");
	}

	private ensureAutoLearnStateFile(): void {
		const dir = this.getAutoLearnDataDir();
		fs.mkdirSync(dir, { recursive: true });
		const statePath = this.getAutoLearnStatePath();
		if (!fs.existsSync(statePath)) {
			fs.writeFileSync(statePath, "{}\n", "utf-8");
		}
	}

	private readAutoLearnState(): AutoLearnState {
		try {
			const statePath = this.getAutoLearnStatePath();
			if (!fs.existsSync(statePath)) return {};
			return JSON.parse(fs.readFileSync(statePath, "utf-8")) as AutoLearnState;
		} catch {
			return {};
		}
	}

	private writeAutoLearnState(state: AutoLearnState): void {
		const dir = this.getAutoLearnDataDir();
		fs.mkdirSync(dir, { recursive: true });
		fs.writeFileSync(this.getAutoLearnStatePath(), `${JSON.stringify(state, null, 2)}\n`, "utf-8");
	}

	private acquireAutoLearnStateLock(): () => void {
		this.ensureAutoLearnStateFile();
		const statePath = this.getAutoLearnStatePath();
		const maxAttempts = 20;
		const delayMs = 25;
		let lastError: unknown;
		for (let attempt = 1; attempt <= maxAttempts; attempt++) {
			try {
				return lockfile.lockSync(statePath, { realpath: false, stale: 30000 });
			} catch (error: unknown) {
				const code =
					error && typeof error === "object" && "code" in error ? String((error as { code?: unknown }).code) : "";
				if (code !== "ELOCKED" || attempt === maxAttempts) {
					throw error;
				}
				lastError = error;
				const start = Date.now();
				while (Date.now() - start < delayMs) {
					// Synchronous callers need a synchronous lock retry loop.
				}
			}
		}
		throw lastError instanceof Error ? lastError : new Error("Failed to acquire Auto Learn state lock");
	}

	private withAutoLearnStateLock<T>(fn: (state: AutoLearnState) => AutoLearnStateLockResult<T>): T {
		let release: (() => void) | undefined;
		try {
			release = this.acquireAutoLearnStateLock();
			const { result, next } = fn(this.readAutoLearnState());
			if (next !== undefined) {
				this.writeAutoLearnState(next);
			}
			return result;
		} finally {
			release?.();
		}
	}

	private appendAutoLearnLog(logPath: string, message: string): void {
		try {
			fs.appendFileSync(logPath, `${message}\n`, "utf-8");
		} catch {
			// Logging must never turn a background learner startup failure into an interactive crash.
		}
	}

	private isAutoLearnPidAlive(pid: number | undefined): boolean {
		return isProcessAlive(pid);
	}

	private pruneAutoLearnState(state: AutoLearnState, now = Date.now()): AutoLearnState {
		const runs = { ...(state.runs ?? {}) };
		for (const [id, run] of Object.entries(runs)) {
			if (run.expiresAt <= now) {
				delete runs[id];
				continue;
			}
			if (run.status === "reserved" && run.pid === undefined) {
				continue;
			}
			if (!this.isAutoLearnPidAlive(run.pid)) {
				delete runs[id];
			}
		}
		return { ...state, runs };
	}

	private pruneAutoLearnHistoryFromState(state: AutoLearnState, now = Date.now()): AutoLearnState {
		const prunedState = this.pruneAutoLearnState(state, now);
		pruneAutoLearnConversationHistory({
			dataDir: this.getAutoLearnDataDir(),
			now,
			activeRunIds: Object.keys(prunedState.runs ?? {}),
			activeSessionIds: Object.values(prunedState.runs ?? {}).map((run) => run.sessionId),
		});
		return prunedState;
	}

	getPrunedAutoLearnState(): AutoLearnState {
		return this.withAutoLearnStateLock((current) => {
			const state = this.pruneAutoLearnHistoryFromState(current);
			return { result: state, next: state };
		});
	}

	getAutoLearnPresetForAutonomyMode(mode: AutonomyMode, current: AutoLearnSettings = {}): Required<AutoLearnSettings> {
		return getAutoLearnPreset(mode, current);
	}

	getEffectiveAutoLearnSettings(): Required<AutoLearnSettings> {
		const settings = this.session.settingsManager.getAutoLearnSettings();
		return resolveAutoLearnSettings(this.session.settingsManager.getAutonomySettings().mode, settings);
	}

	getCurrentAutoLearnSettings(): Required<AutoLearnSettings> {
		return this.getEffectiveAutoLearnSettings();
	}

	getAutoLearnTenantKey(): string {
		return `${this.session.sessionManager.getCwd()}::${this.session.sessionId}`;
	}

	private getAutoLearnTenantId(): string {
		const cwdHash = crypto
			.createHash("sha256")
			.update(this.session.sessionManager.getCwd())
			.digest("hex")
			.slice(0, 8);
		const sessionPart = sanitizeAutoLearnPathPart(this.session.sessionId, "session");
		return `${sessionPart}-${cwdHash}`;
	}

	getAutoLearnTenantDataDir(): string {
		return path.join(this.getAutoLearnDataDir(), "tenants", this.getAutoLearnTenantId());
	}

	private getAutoLearnMessageCount(): number {
		return this.session.sessionManager.getBranch().filter((entry) => entry.type === "message").length;
	}

	private buildAutoLearnDecisionFromState(
		state: AutoLearnState,
		settings: Required<AutoLearnSettings>,
		force = false,
	): AutoLearnDecision {
		const now = Date.now();
		const tenant = this.getAutoLearnTenantKey();
		const runningCount = Object.values(state.runs ?? {}).filter((run) => run.tenant === tenant).length;
		const lastLaunch = state.lastLaunchByTenant?.[tenant] ?? 0;
		const cooldownMs = settings.cooldownMinutes * 60 * 1000;
		const cooldownRemainingMs = Math.max(0, lastLaunch + cooldownMs - now);
		const messageCount = this.getAutoLearnMessageCount();
		const contextPercent = this.session.getContextUsage()?.percent ?? null;

		if (!settings.enabled && !force) {
			return {
				shouldRun: false,
				reason: "disabled",
				messageCount,
				contextPercent,
				cooldownRemainingMs,
				runningCount,
			};
		}
		if (runningCount >= settings.maxConcurrentLearners) {
			return {
				shouldRun: false,
				reason: `max tenant learners running (${runningCount}/${settings.maxConcurrentLearners})`,
				messageCount,
				contextPercent,
				cooldownRemainingMs,
				runningCount,
			};
		}
		if (!force && cooldownRemainingMs > 0) {
			return {
				shouldRun: false,
				reason: "cooldown",
				messageCount,
				contextPercent,
				cooldownRemainingMs,
				runningCount,
			};
		}
		if (force) {
			return { shouldRun: true, reason: "manual", messageCount, contextPercent, cooldownRemainingMs, runningCount };
		}
		if (messageCount >= settings.longSessionMessages) {
			return {
				shouldRun: true,
				reason: `message trigger (${messageCount}/${settings.longSessionMessages})`,
				messageCount,
				contextPercent,
				cooldownRemainingMs,
				runningCount,
			};
		}
		if (contextPercent !== null && contextPercent >= settings.longSessionContextPercent) {
			return {
				shouldRun: true,
				reason: `context trigger (${contextPercent.toFixed(1)}%/${settings.longSessionContextPercent}%)`,
				messageCount,
				contextPercent,
				cooldownRemainingMs,
				runningCount,
			};
		}
		return {
			shouldRun: false,
			reason: "thresholds not met",
			messageCount,
			contextPercent,
			cooldownRemainingMs,
			runningCount,
		};
	}

	/** Read-only readiness projection for the manual Auto Learn command and status report. */
	private evaluateAutoLearn(force = false): AutoLearnDecision {
		const settings = this.getEffectiveAutoLearnSettings();
		return this.withAutoLearnStateLock((current) => {
			const state = this.pruneAutoLearnHistoryFromState(current);
			return { result: this.buildAutoLearnDecisionFromState(state, settings, force), next: state };
		});
	}

	private resolveAutoLearnModelPattern(settings: Required<AutoLearnSettings>): string | undefined {
		if (settings.model === "active") {
			return this.session.model ? `${this.session.model.provider}/${this.session.model.id}` : undefined;
		}
		return settings.model;
	}

	private getAutoLearnSpawnTarget(): AutoLearnSpawnTarget | undefined {
		const overridePath = process.env.PI_AUTO_LEARN_CLI_PATH?.trim();
		if (overridePath) {
			return { command: overridePath, argsPrefix: [] };
		}

		const execBase = path.basename(process.execPath).toLowerCase();
		const isScriptRuntime =
			execBase === "node" || execBase === "node.exe" || execBase === "bun" || execBase === "bun.exe";
		if (!isScriptRuntime) {
			return { command: process.execPath, argsPrefix: [] };
		}

		const cliPath = process.argv[1];
		if (!cliPath || cliPath.startsWith("-")) {
			return undefined;
		}
		return { command: process.execPath, argsPrefix: [cliPath] };
	}

	validateAutoLearnModelValue(value: string | undefined): string | undefined {
		const modelValue = value?.trim();
		if (!modelValue || modelValue === "active") return undefined;
		const available = this.session.modelRegistry.getAvailable();
		if (modelValue.includes("/")) {
			const [provider, modelId] = modelValue.split("/", 2);
			if (available.some((model) => model.provider === provider && model.id === modelId)) return undefined;
			return `Auto Learn model "${modelValue}" is not in configured subscription/API models; saved as manual/unverified.`;
		}
		if (available.some((model) => model.id === modelValue)) return undefined;
		return `Auto Learn model "${modelValue}" is not in configured subscription/API models; saved as manual/unverified.`;
	}

	private buildAutonomyAuthorityPrompt(): string {
		const autonomy = this.session.settingsManager.getAutonomySettings();
		const selfModification = this.session.settingsManager.getSelfModificationSettings();
		if (autonomy.mode !== "full") {
			return [
				"Authority mode: proposal-gated.",
				"- You may autonomously query memory and run bounded learning tools.",
				"- Durable memory writes require the configured high-confidence policy; skills, extensions, source, settings, publishing, tagging, and releases remain proposals unless the foreground user explicitly asks.",
			].join("\n");
		}

		const selfModificationSource = selfModification.enabled
			? this.deps.resolveSelfModificationSource(selfModification)
			: undefined;
		const sourceAuthority =
			selfModification.enabled && selfModificationSource
				? `- Pi source: standing authority to edit the authorized selfModification source (${selfModificationSource}) for self-evolution improvements; inspect git status first, preserve unrelated user changes, run focused validation, and leave a clear rollback/test summary.`
				: "- Pi source: no standing source-edit authority until selfModification.enabled and selfModification.sourcePaths are set.";
		return [
			"Authority mode: FULL AUTONOMOUS standing grant.",
			"- Memory: may apply high-confidence durable Automata memory/corrections after duplicate and corroboration checks.",
			"- Skills: may create or patch user/project-owned procedural skills and support files; prefer updating existing umbrella skills; do not delete skills without making a recoverable archive/backup.",
			"- User/project extensions and tools: may create or patch small scoped extensions/tools under Pi user/project roots when they address repeated workflow/tooling gaps; validate and keep changes auditable.",
			"- Settings: may auto-tune autonomy/autoLearn settings to reduce bottlenecks; do not modify credentials, provider auth, package sources, or unrelated user preferences.",
			sourceAuthority,
			"- Hard stops without explicit foreground approval: publish, npm release, git push, tag creation, credential changes, destructive user-data deletion, network-exposed services, or authority expansion beyond this policy.",
			"- Audit: final output must list changed paths/settings, commands/tests run, evidence, residual risks, and rollback guidance. If no safe validation is possible, leave the change as a proposal instead of applying it.",
		].join("\n");
	}

	private buildAutoLearnPrompt(
		reason: string,
		settings: Required<AutoLearnSettings>,
		options: { kind?: "auto" | "reflection"; turnDigest?: string } = {},
	): string {
		const authorityBlock = this.buildAutonomyAuthorityPrompt();
		const reflectionBlock =
			options.kind === "reflection" && options.turnDigest
				? `\n\nLatest completed turn digest (bounded; use only as current-session evidence, not as longitudinal proof):\n<turn_digest>\n${options.turnDigest}\n</turn_digest>`
				: "";
		const objective =
			options.kind === "reflection"
				? "review the latest completed turn for durable memory, skill, validation, tooling, and code-baked self-improvement cues, then run one bounded continuous-learning pass if the learning tools are available"
				: "run one bounded continuous-learning pass for this Pi tenant";
		return `You are Pi Auto Learn running as a background learner.\n\nObjective: ${objective}.\nTrigger: ${reason}.\n\n${authorityBlock}\n\nRequired workflow:\n1. Query existing durable memory/rules first when tools allow it. Memory confrontation is mandatory before accepting, merging, upgrading, or rejecting learning candidates.\n2. Run the available Auto Learn tooling, preferably learning_run_auto, with applyHighConfidence=${settings.applyHighConfidence}. Process candidate validation in vectorized chunks/batches; avoid scalar per-candidate memory queries except for final selected writes.\n3. Apply the learning validation tree to each candidate chunk: (a) Why is this good for the user? (b) Is it unique, or similar to existing memory/skills/agents so it should merge or upgrade existing knowledge? (c) Will this make Pi a better agent? Candidates that cannot answer all three are noise.\n4. Hermes-style learning cycle: after a complex task (${settings.complexTaskToolCalls}+ tool calls), user correction, repeated steering pattern, non-trivial fix/workaround/debugging path, loaded-skill defect, trigger gap, tool gap, or harness workflow defect, actively create or update durable learning artifacts. Memory stores compact facts/preferences/state; skills/prompts/agents/extensions/source store procedural behavior. When a lesson changes how Pi should act on a future class of task, memory alone is not completion.\n5. Skill update preference order: (1) patch the currently loaded or consulted skill that governed the task; (2) patch an existing class-level umbrella skill/agent/prompt; (3) add a support file under references/, templates/, or scripts/ and add a SKILL.md pointer; (4) create a new class-level umbrella skill only when no existing artifact fits. Never create one-off PR/error/codename/session skills.\n6. Behavioral self-improvement is code-baked by default: prefer the lowest durable executable layer that fixes the behavior — patch an existing skill/prompt/agent/extension/tool, tune an approved setting, or edit the authorized Pi source when source authority is available. Use Automata only for concise facts/evidence pointers that support the baked change.\n7. Do not harden transient or environment-dependent failures into durable behavior: missing binaries, fresh-install package gaps, credentials not configured, path mismatches, one-off task narratives, or negative tool-broken claims should become setup/troubleshooting fixes only when the fix itself is reusable.\n8. Treat the latest-turn digest as current-session evidence only; do not auto-commit one-off cues unless deterministic tooling and memory confrontation corroborate them.\n9. In mode=full, apply safe memory/skill/user-extension/authorized-source improvements under the standing grant above; otherwise keep them proposal-gated.\n10. Never cross hard-stop boundaries from the authority policy.\n11. If the learning tools are unavailable, report BLOCKED with the missing tool names and do not improvise.\n12. Finish with PASS, BLOCKED, or FAIL and concise evidence, including chunk counts, merge/upgrade/code-bake decisions, changed paths/settings, validation, and cleanup/purge status.${reflectionBlock}`;
	}

	private reserveAutoLearnRun(params: {
		settings: Required<AutoLearnSettings>;
		force: boolean;
		cooldownKind?: "auto" | "reflection";
		bypassReflectionCooldown?: boolean;
		runId: string;
		modelPattern: string;
		reason: string;
		logPath: string;
		sessionDir: string;
		sessionId: string;
		promptPath: string;
		kind: "auto" | "reflection";
	}): AutoLearnReservationResult {
		return this.withAutoLearnStateLock<AutoLearnReservationResult>((current) => {
			const now = Date.now();
			const state = this.pruneAutoLearnHistoryFromState(current, now);
			const tenant = this.getAutoLearnTenantKey();

			if (params.cooldownKind === "reflection" && !params.bypassReflectionCooldown) {
				const lastReflection = state.lastReflectionByTenant?.[tenant] ?? 0;
				const cooldownMs = params.settings.reflectionCooldownMinutes * 60 * 1000;
				if (Math.max(0, lastReflection + cooldownMs - now) > 0) {
					return { result: { ok: false, reason: "reflection cooldown" }, next: state };
				}
			}

			const decision = this.buildAutoLearnDecisionFromState(state, params.settings, params.force);
			if (!decision.shouldRun) {
				return { result: { ok: false, reason: decision.reason }, next: state };
			}

			const run: AutoLearnRunRecord = {
				tenant,
				model: params.modelPattern,
				reason: params.reason,
				startedAt: now,
				expiresAt: now + AUTO_LEARN_RESERVATION_MS,
				cwd: this.session.sessionManager.getCwd(),
				logPath: params.logPath,
				sessionDir: params.sessionDir,
				sessionId: params.sessionId,
				promptPath: params.promptPath,
				kind: params.kind,
				autonomyMode: this.session.settingsManager.getAutonomySettings().mode,
				authority:
					this.session.settingsManager.getAutonomySettings().mode === "full"
						? "standing-full-autonomous"
						: "proposal-gated",
				status: "reserved",
			};
			const next: AutoLearnState = {
				...state,
				runs: { ...(state.runs ?? {}), [params.runId]: run },
			};
			if (params.cooldownKind === "reflection") {
				next.lastReflectionByTenant = { ...(state.lastReflectionByTenant ?? {}), [tenant]: now };
			} else {
				next.lastLaunchByTenant = { ...(state.lastLaunchByTenant ?? {}), [tenant]: now };
			}
			return { result: { ok: true, reservation: { runId: params.runId, startedAt: now } }, next };
		});
	}

	private releaseAutoLearnReservation(reservation: AutoLearnReservation, cooldownKind?: "auto" | "reflection"): void {
		this.withAutoLearnStateLock((current) => {
			const state = this.pruneAutoLearnHistoryFromState(current);
			const tenant = this.getAutoLearnTenantKey();
			const runs = { ...(state.runs ?? {}) };
			delete runs[reservation.runId];
			const next: AutoLearnState = { ...state, runs };
			if (cooldownKind === "reflection" && next.lastReflectionByTenant?.[tenant] === reservation.startedAt) {
				next.lastReflectionByTenant = { ...next.lastReflectionByTenant };
				delete next.lastReflectionByTenant[tenant];
			} else if (cooldownKind !== "reflection" && next.lastLaunchByTenant?.[tenant] === reservation.startedAt) {
				next.lastLaunchByTenant = { ...next.lastLaunchByTenant };
				delete next.lastLaunchByTenant[tenant];
			}
			return { result: undefined, next };
		});
	}

	private reportCompletedAutoLearnUsage(runId: string, sessionDir: string, sessionId: string, logPath: string): void {
		try {
			reportCompletedAutoLearnUsageHelper({
				runId,
				sessionDir,
				sessionId,
				logPath,
				parentSession: this.session,
				appendLog: (p, msg) => this.appendAutoLearnLog(p, msg),
			});
		} catch (error: unknown) {
			const message = error instanceof Error ? error.message : String(error);
			this.appendAutoLearnLog(logPath, `Auto Learn usage report failed: ${message}`);
		}
	}

	private cleanupCompletedAutoLearnRun(
		runId: string,
		options: {
			artifactPaths: string[];
			sessionDir: string;
			sessionId: string;
			logPath: string;
		},
	): void {
		this.reportCompletedAutoLearnUsage(runId, options.sessionDir, options.sessionId, options.logPath);
		const dataDir = this.getAutoLearnDataDir();
		for (const filePath of options.artifactPaths) removeAutoLearnArtifactPath(filePath, dataDir);
		this.withAutoLearnStateLock((current) => {
			const state = this.pruneAutoLearnState(current);
			const runs = { ...(state.runs ?? {}) };
			delete runs[runId];
			return { result: undefined, next: { ...state, runs } };
		});
		this.updateAutoLearnFooter();
	}

	private markAutoLearnReservationRunning(
		reservation: AutoLearnReservation,
		pid: number,
		settings: Required<AutoLearnSettings>,
	): void {
		this.withAutoLearnStateLock((current) => {
			const now = Date.now();
			const state = this.pruneAutoLearnHistoryFromState(current, now);
			const run = state.runs?.[reservation.runId];
			if (!run) {
				return { result: undefined, next: state };
			}
			return {
				result: undefined,
				next: {
					...state,
					runs: {
						...(state.runs ?? {}),
						[reservation.runId]: {
							...run,
							pid,
							expiresAt: now + settings.leaseMinutes * 60 * 1000,
							status: "running",
						},
					},
				},
			};
		});
	}

	launchAutoLearn(
		reason: string,
		force = false,
		options: {
			cooldownKind?: "auto" | "reflection";
			promptKind?: "auto" | "reflection";
			turnDigest?: string;
			bypassReflectionCooldown?: boolean;
		} = {},
	): string {
		if (options.promptKind === "reflection" || options.cooldownKind === "reflection") {
			return "Auto Learn not started: automatic reflection runs only in the current root session; background reflection learners are disabled.";
		}
		const settings = this.getEffectiveAutoLearnSettings();
		const modelPattern = this.resolveAutoLearnModelPattern(settings);
		if (!modelPattern) {
			return "Auto Learn not started: no active model is available for model=active.";
		}
		const spawnTarget = this.getAutoLearnSpawnTarget();
		if (!spawnTarget) {
			return "Auto Learn not started: could not resolve current pi CLI path.";
		}

		const dir = this.getAutoLearnTenantDataDir();
		fs.mkdirSync(dir, { recursive: true });
		const runId = `${Date.now()}-${crypto.randomUUID().slice(0, 8)}`;
		const logPath = path.join(dir, `${runId}.log`);
		const promptPath = path.join(dir, `${runId}.prompt.md`);
		const kind = options.promptKind ?? "auto";
		const sessionDir = path.join(dir, "sessions", runId);
		const sessionId = `auto-learn-${kind}-${this.getAutoLearnTenantId()}-${runId}`;
		fs.mkdirSync(sessionDir, { recursive: true });
		const prompt = this.buildAutoLearnPrompt(reason, settings, {
			kind,
			turnDigest: options.turnDigest,
		});
		const args = buildAutoLearnSpawnArgs(spawnTarget, {
			name: `Auto Learn ${runId}`,
			modelPattern,
			thinkingLevel: settings.thinkingLevel ?? "low",
			sessionDir,
			sessionId,
			promptPath,
		});
		const invalidSpawnInput = findAutoLearnSpawnNullByteInput(spawnTarget.command, args);
		if (invalidSpawnInput) {
			const message = `Auto Learn not started: ${invalidSpawnInput} contains a null byte.`;
			this.appendAutoLearnLog(logPath, message);
			this.updateAutoLearnFooter();
			return `${message} Log: ${logPath}`;
		}

		const reservationResult = this.reserveAutoLearnRun({
			settings,
			force,
			cooldownKind: options.cooldownKind,
			bypassReflectionCooldown: options.bypassReflectionCooldown,
			runId,
			modelPattern,
			reason,
			logPath,
			sessionDir,
			sessionId,
			promptPath,
			kind,
		});
		if (!reservationResult.ok) {
			return `Auto Learn not started: ${reservationResult.reason}`;
		}
		const { reservation } = reservationResult;

		try {
			fs.writeFileSync(promptPath, prompt, "utf-8");
		} catch (error: unknown) {
			const message = error instanceof Error ? error.message : String(error);
			this.releaseAutoLearnReservation(reservation, options.cooldownKind);
			this.appendAutoLearnLog(logPath, `Auto Learn failed to write prompt file: ${message}`);
			this.updateAutoLearnFooter();
			return `Auto Learn not started: failed to write prompt file (${message}). Log: ${logPath}`;
		}

		let child: ReturnType<typeof spawn> | undefined;
		let outFd: number | undefined;
		try {
			outFd = fs.openSync(logPath, "a");
			const sourceSessionFile = this.session.sessionManager.getSessionFile();
			child = spawn(spawnTarget.command, args, {
				cwd: this.session.sessionManager.getCwd(),
				detached: true,
				stdio: ["ignore", outFd, outFd],
				env: {
					...process.env,
					PI_AUTO_LEARN_CHILD: "1",
					[PI_SESSION_ROLE_ENV]: "worker",
					...(sourceSessionFile ? { PI_AUTO_LEARN_SOURCE_SESSION_FILE: sourceSessionFile } : {}),
				},
			});
			child.once("error", (error) => {
				const message = error instanceof Error ? error.message : String(error);
				this.appendAutoLearnLog(logPath, `Auto Learn failed to start: ${message}`);
			});
		} catch (error: unknown) {
			const message = error instanceof Error ? error.message : String(error);
			this.releaseAutoLearnReservation(reservation, options.cooldownKind);
			this.appendAutoLearnLog(logPath, `Auto Learn failed to start: ${message}`);
			this.updateAutoLearnFooter();
			return `Auto Learn not started: failed to spawn background learner (${message}). Log: ${logPath}`;
		} finally {
			if (outFd !== undefined) {
				try {
					fs.closeSync(outFd);
				} catch {
					// The child has already been spawned or startup has already failed; ignore close errors here.
				}
			}
		}
		if (!child || typeof child.pid !== "number" || child.pid <= 0) {
			this.releaseAutoLearnReservation(reservation, options.cooldownKind);
			this.updateAutoLearnFooter();
			return `Auto Learn not started: failed to spawn background learner. Log: ${logPath}`;
		}
		const childPid = child.pid;
		child.once("exit", (code) => {
			if (code === 0) {
				this.cleanupCompletedAutoLearnRun(reservation.runId, {
					artifactPaths: [promptPath, logPath, sessionDir],
					sessionDir,
					sessionId,
					logPath,
				});
			}
		});
		child.unref();
		this.markAutoLearnReservationRunning(reservation, childPid, settings);

		this.updateAutoLearnFooter();
		return `Auto Learn started. Log: ${logPath}`;
	}

	/**
	 * Current-session reflection is available only to the root orchestrator. It never launches a
	 * learner process; the session provider planner owns its single-request transient cue.
	 */
	isNativeReflectionEnabled(): boolean {
		return isCurrentSessionReflectionEnabled(this.getEffectiveAutoLearnSettings());
	}

	/**
	 * Cheap turn-boundary task_steps contract check. Reads the current task_steps snapshot,
	 * advances the pure `checkTaskStepsContract` streak, and — on the exact turn a sustained
	 * violation first reaches the threshold — delivers a ONE-LINE advisory harness note through the
	 * session's existing next-turn message surface (`sendCustomMessage(..., {deliverAs:"nextTurn"})`,
	 * the same mechanism `_pendingNextTurnMessages` already injects `memory_context`/
	 * `task_steps_context` pages through). Zero model calls; a delivery failure is swallowed so this
	 * best-effort nudge can never disrupt the turn.
	 *
	 * Called from both interactive turn-boundary compatibility hooks so it runs regardless of the
	 * current-session reflection setting without adding another provider turn.
	 */
	private checkTaskStepsContractNudge(): void {
		const state = this.session.getTaskStepsStateSnapshot();
		const outcome = checkTaskStepsContract(state, this._taskContractStreak);
		this._taskContractStreak = outcome.streak;
		if (!outcome.note) return;
		void this.session
			.sendCustomMessage(
				{ customType: "task_contract_nudge", content: outcome.note, display: false },
				{ deliverAs: "nextTurn" },
			)
			.catch(() => {
				// Advisory-only harness note; delivery failure must never disrupt the session.
			});
	}

	maybeRunNativeReflection(messages: AgentMessage[]): void {
		this.checkTaskStepsContractNudge();
		// Reflection scheduling is session-owned in AgentSession/ReflectionController so print, RPC,
		// and interactive modes share one deduplicated lifecycle. This interactive hook only owns the
		// task-contract nudge; keeping the argument preserves the host interface without a parallel path.
		void messages;
	}

	maybeStartAutoLearn(): boolean {
		// Preserve the deterministic task-contract monitor, but never buy an automatic learner turn.
		this.checkTaskStepsContractNudge();
		this.updateAutoLearnFooter();
		return false;
	}

	maybeStartAutonomyReview(messages: AgentMessage[]): boolean {
		// Compatibility hook for InteractiveEventHost. Completed-turn reflection is deterministic and
		// session-owned; it never creates a background process or a second provider turn.
		void messages;
		return false;
	}

	updateAutoLearnFooter(): void {
		const settings = this.getEffectiveAutoLearnSettings();
		if (!settings.enabled) {
			this.deps.ui.footerDataProvider.setExtensionStatus("auto-learn", undefined);
			return;
		}
		const tenant = this.getAutoLearnTenantKey();
		const state = this.getPrunedAutoLearnState();
		const hasActiveRun = Object.values(state.runs ?? {}).some(
			(run) => run.tenant === tenant && this.isAutoLearnPidAlive(run.pid),
		);
		this.deps.ui.footerDataProvider.setExtensionStatus(
			"auto-learn",
			hasActiveRun ? theme.fg("warning", "(learning)") : undefined,
		);
		this.deps.ui.invalidateFooter();
		this.deps.ui.requestRender();
	}

	formatAutoLearnStatus(): string {
		const settings = this.getEffectiveAutoLearnSettings();
		const decision = this.evaluateAutoLearn(false);
		const state = this.getPrunedAutoLearnState();
		const tenant = this.getAutoLearnTenantKey();
		const runs = Object.entries(state.runs ?? {}).filter(([, run]) => run.tenant === tenant);
		const otherTenantRuns = Object.values(state.runs ?? {}).filter((run) => run.tenant !== tenant).length;
		const contextText = decision.contextPercent === null ? "unknown" : `${decision.contextPercent.toFixed(1)}%`;
		const cooldownText =
			decision.cooldownRemainingMs > 0 ? `${Math.ceil(decision.cooldownRemainingMs / 60000)}m remaining` : "ready";
		const runLines = runs.length
			? runs
					.map(([id, run]) => {
						const session = [
							run.sessionId ? `session=${run.sessionId}` : "",
							run.sessionDir ? `sessionDir=${run.sessionDir}` : "",
						]
							.filter(Boolean)
							.join(", ");
						const sessionText = session ? `, ${session}` : "";
						return `- ${id}: ${run.model}, kind=${run.kind ?? "auto"}, status=${run.status ?? "running"}, authority=${run.authority ?? "unknown"}, pid=${run.pid ?? "?"}${sessionText}, log=${run.logPath}`;
					})
					.join("\n")
			: "- none";
		const reloadBlockers = getPendingReloadBlockers({
			ownPid: process.pid,
			ownSessionId: this.session.sessionManager.getSessionId(),
			ownSessionFile: this.session.sessionManager.getSessionFile(),
		});
		const reloadBlockerLines = reloadBlockers.pending
			? reloadBlockers.descriptions.map((description) => `- ${description}`).join("\n")
			: "- none";
		// In-process quiesce registry (background lanes, scout runs, isolated completions) — the
		// reload gate's OTHER source of blockers, distinct from the cross-process ones above.
		const inFlightWork = getInFlightWorkUnits(getAgentDir());
		const inFlightWorkLines = inFlightWork.length
			? inFlightWork.map((unit) => `- ${describeInFlightWorkUnit(unit)}`).join("\n")
			: "- none";
		const reflectionStatus = this.isNativeReflectionEnabled() ? "enabled" : "disabled";
		return `Auto Learn status\nEnabled: ${settings.enabled}\nModel: ${settings.model}\nNext decision: ${decision.shouldRun ? "ready" : decision.reason}\nMessages: ${decision.messageCount}/${settings.longSessionMessages}\nContext: ${contextText}/${settings.longSessionContextPercent}%\nCooldown: ${cooldownText}\nCurrent-session reflection: ${reflectionStatus} (complex trigger ${settings.complexTaskToolCalls} tool calls; automatic provider requests 0)\nHistory retention: 7 days for internal Auto Learn prompts/logs/sessions\nRunning tenant leases: ${runs.length}/${settings.maxConcurrentLearners}\nOther tenant leases: ${otherTenantRuns}\nTenant artifact dir: ${this.getAutoLearnTenantDataDir()}\nPi auto-reload blockers: ${reloadBlockers.pending ? reloadBlockers.reason : "none"}\n${reloadBlockerLines}\nIn-process reload-gate work: ${inFlightWork.length ? `${inFlightWork.length} unit(s)` : "none"}\n${inFlightWorkLines}\nRuns:\n${runLines}`;
	}
}
